export const SITE_PAGES = {
  HOME: "home",
  API_DOCS: "api_docs",
  GUIDES: "guides",
  // A framework landing page, /integrations/<id> (site-contract v5).
  INTEGRATION: "integration",
  // /platforms and /platforms/<id> (site-contract v8).
  PLATFORMS: "platforms",
  // /processors and /processors/<id>: site-owned, config/processors.json.
  PROCESSORS: "processors"
} as const

export type SitePage = (typeof SITE_PAGES)[keyof typeof SITE_PAGES]

// The framework landing pages live under this prefix; the homepage grid that
// lists them is the anchor.
export const INTEGRATIONS_PATH = "/integrations"
// The /learn index (LearnController). The articles themselves come from the
// server in the bootstrap (learn_pages), so a new one needs no change here.
export const LEARN_PATH = "/learn"
export const FRAMEWORKS_HASH = "frameworks"
export const integrationPath = (id: string): string => `${INTEGRATIONS_PATH}/${id}`

// Where an app is built or hosted (contract v8): the index and one page per
// platforms[] row that has one. The homepage section is the anchor.
export const PLATFORMS_PATH = "/platforms"
export const PLATFORMS_HASH = "platforms"
export const platformPath = (id: string): string => `${PLATFORMS_PATH}/${id}`

// The homepage FAQ. Each question is an anchor of its own too ("faq-withdraw"),
// named in config/landing_copy.json.
export const FAQ_HASH = "faq"

// The payment processor pages ("OpenReceive + Stripe"): the index and one page
// per live row in config/processors.json. The homepage section is the anchor.
export const PROCESSORS_PATH = "/processors"
export const PROCESSORS_HASH = "processors"
export const processorPath = (id: string): string => `${PROCESSORS_PATH}/${id}`

export const BOOTSTRAP_SCRIPT_ID = "__app_bootstrap"

// The coding-agents entrypoint. The site contract (v3) publishes it at /agents
// rather than /guides/agents, but it is rendered exactly like a guide, so the
// routing layer maps that one slug to its own path.
export const AGENTS_SLUG = "agents"
export const AGENTS_PATH = "/agents"

// The BTCPay Server home (contract v4): the plugin's own README, published at
// /btcpay rather than /guides/btcpay and rendered exactly like a guide.
export const BTCPAY_SLUG = "btcpay"
export const BTCPAY_PATH = "/btcpay"
// Its landing page, which /btcpay names as canonical (SearchIndex): the nav
// links here, so the site's own links do not point at the duplicate.
export const BTCPAY_FRAMEWORK_ID = "btcpay-server"

// The WordPress + WooCommerce plugin overview (contract v6), also rendered
// like a guide at its own path.
export const WORDPRESS_SLUG = "wordpress"
export const WORDPRESS_PATH = "/wordpress"

// Every guide the contract publishes at its own path, slug → path. The routing
// layer maps these both ways; everything else is /guides/<slug>.
export const OWN_PATH_GUIDES: Record<string, string> = {
  [AGENTS_SLUG]: AGENTS_PATH,
  [BTCPAY_SLUG]: BTCPAY_PATH,
  [WORDPRESS_SLUG]: WORDPRESS_PATH
}

export const NWC_PATH = "/get_a_nwc_code_to_receive_payments"
export const SWAP_PROVIDER_PATH = "/set_up_swap_provider"
export const GITHUB_URL = "https://github.com/OpenReceive/openreceive"
export const TELEGRAM_URL = "https://t.me/openreceive"
export const TELEGRAM_HANDLE = "@openreceive"
export const SUPPORT_EMAIL = "info@openreceive.org"
export const DISCORD_URL = "https://discord.gg/p4uDDD95U8"

export const PAGE_TITLES: Record<SitePage, string> = {
  // SocialMetaHelper::DEFAULT_TITLE on the server.
  home: "OpenReceive: Open-Source Bitcoin, USDT & USDC Payments",
  api_docs: "API docs — OpenReceive",
  guides: "Guides — OpenReceive",
  // Overridden per framework by AppWorkspace.pageTitle; this is the fallback
  // for an id the bundle does not carry.
  integration: "Frameworks — OpenReceive",
  // The index; a platform page is titled by AppWorkspace.pageTitle.
  platforms: "Tested on the platforms you build with — OpenReceive",
  // The index; a processor page is titled by AppWorkspace.pageTitle.
  processors: "Add BTC, USDT & USDC next to the processor you already use — OpenReceive"
}

export const isSitePage = (value: string | null | undefined): value is SitePage =>
  value === SITE_PAGES.HOME ||
  value === SITE_PAGES.API_DOCS ||
  value === SITE_PAGES.GUIDES ||
  value === SITE_PAGES.INTEGRATION ||
  value === SITE_PAGES.PLATFORMS ||
  value === SITE_PAGES.PROCESSORS
